import React, { useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { loadMermaid } from './mermaidLoader';

// Rendering diagrammi Mermaid come SVG. Streaming-safe: il parse è debounced e
// il sorgente parziale/invalido NON crasha mai — resta visibile l'ultimo SVG
// valido oppure il sorgente testuale con nota esplicita (errore mai inghiottito
// in silenzio).

const RENDER_DEBOUNCE_MS = 250;

interface MermaidBlockProps {
  code: string;
  className?: string;
}

type RenderState =
  | { status: 'pending' }
  | { status: 'ok'; svg: string }
  | { status: 'invalid'; error: string; lastGoodSvg: string | null };

export const MermaidBlock: React.FC<MermaidBlockProps> = ({ code, className }) => {
  const [state, setState] = useState<RenderState>({ status: 'pending' });
  const lastGoodRef = useRef<string | null>(null);
  const reactId = useId();

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const mermaid = await loadMermaid();
          await mermaid.parse(code);
          const domId = `mmd-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}-${Date.now() % 100000}`;
          const { svg } = await mermaid.render(domId, code);
          if (cancelled) return;
          lastGoodRef.current = svg;
          setState({ status: 'ok', svg });
        } catch (err) {
          if (cancelled) return;
          const messageRaw = err instanceof Error ? err.message : String(err);
          setState({
            status: 'invalid',
            error: messageRaw.split('\n')[0] ?? 'Sintassi non valida',
            lastGoodSvg: lastGoodRef.current,
          });
        }
      })();
    }, RENDER_DEBOUNCE_MS);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [code, reactId]);

  if (state.status === 'ok' || (state.status === 'invalid' && state.lastGoodSvg)) {
    const svg = state.status === 'ok' ? state.svg : state.lastGoodSvg as string;
    return (
      <div className={`not-prose bg-card rounded-lg border border-border overflow-x-auto ${className ?? ''}`}>
        <div
          className="p-3 [&_svg]:max-w-full [&_svg]:h-auto"
          data-testid="mermaid-svg"
          // SVG prodotto da mermaid con securityLevel 'strict' (sanificato dalla lib).
          dangerouslySetInnerHTML={{ __html: svg }}
        />
        {state.status === 'invalid' && (
          <div className="px-3 pb-2 text-[11px] text-amber-600 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> Aggiornamento diagramma in corso…
          </div>
        )}
      </div>
    );
  }

  // Pending o invalido senza render precedente: sorgente visibile, mai vuoto.
  return (
    <div className={`not-prose bg-muted rounded-lg border border-border ${className ?? ''}`}>
      <pre className="m-0 p-3 text-xs leading-relaxed overflow-x-auto text-muted-foreground" data-testid="mermaid-source">
        <code>{code}</code>
      </pre>
      {state.status === 'invalid' && (
        <div className="px-3 pb-2 text-[11px] text-amber-600 flex items-center gap-1" data-testid="mermaid-error">
          <AlertTriangle className="h-3 w-3" /> Diagramma non renderizzabile: {state.error}
        </div>
      )}
    </div>
  );
};
